import type { MetricStatus, MetricDirection } from '@/lib/scorecards/status';
import type { InsightsMetric } from '@/api/insights.api';

/**
 * Shared, component-free view helpers for the Insights dashboard —
 * status → kw-token maps and the metric value formatter. Kept in a
 * plain `.ts` module (not a component file) so it's a single source of
 * truth across KpiCard / TrendChart / InsightsPage without tripping the
 * react-refresh "only export components" rule.
 */

// ── Status → kw-* tokens ─────────────────────────────────────────────────

export const STATUS_LABEL: Record<MetricStatus, string> = {
  green: 'On track',
  yellow: 'At risk',
  red: 'Off track',
};

/** Solid dot colour. */
export const STATUS_DOT_CLASS: Record<MetricStatus, string> = {
  green: 'bg-kw-emerald',
  yellow: 'bg-kw-warning-text',
  red: 'bg-kw-danger-text',
};

/** Foreground colour — also drives `currentColor` for sparklines/charts. */
export const STATUS_TEXT_CLASS: Record<MetricStatus, string> = {
  green: 'text-kw-emerald',
  yellow: 'text-kw-warning-text',
  red: 'text-kw-danger-text',
};

/** Chip (bg + text) — matches the scorecard grid cells. */
export const STATUS_CHIP_CLASS: Record<MetricStatus, string> = {
  green: 'bg-kw-emerald-light text-kw-tag-text',
  yellow: 'bg-kw-warning-bg text-kw-warning-text',
  red: 'bg-kw-danger-bg text-kw-danger-text',
};

// ── Value formatting ─────────────────────────────────────────────────────

/**
 * Format a numeric metric value for display, keyed by its `unit`.
 * Mirrors the private formatter on the scorecards grid so a value reads
 * the same in both places.
 */
export function formatMetricValue(value: number, metric: Pick<InsightsMetric, 'unit' | 'unitLabel'>): string {
  switch (metric.unit) {
    case 'dollars':
      return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    case 'percent':
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'ratio':
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    case 'custom':
      return `${value.toLocaleString()} ${metric.unitLabel ?? ''}`.trim();
    case 'count':
    default:
      return value.toLocaleString();
  }
}

/**
 * Direction-aware tone for a week-over-week delta: a rise on a
 * higher-is-better metric is good; a rise on a lower-is-better metric
 * is bad. A flat/zero delta reads neutral.
 */
export function deltaTone(deltaPct: number, direction: MetricDirection): 'good' | 'bad' | 'neutral' {
  if (deltaPct === 0) return 'neutral';
  const improving = direction === 'higher_is_better' ? deltaPct > 0 : deltaPct < 0;
  return improving ? 'good' : 'bad';
}
